import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEOHead from './SEOHead';
import Breadcrumb from './Breadcrumb';

/**
 * Shared layout for /tecnología product pages (Lixo, HALO-I, Digital Twin, Canaria+,
 * Exoesqueleto, Robótica, KlinLoop...). Keeps visual consistency with TrisTrash/Pando
 * without duplicating the whole JSX structure per page.
 */
export default function ProductPage({
  testId,
  path,
  seoTitle,
  seoDescription,
  jsonLd = [],
  locale = 'es',
  alternates = [],
  breadcrumbItems,
  breadcrumbLabel,
  tag,
  titleMain,
  titleAccent,
  lead,
  problema,
  problemaLabel = 'El problema',
  comoFuncionaLabel = 'Cómo funciona',
  comoFunciona = [],
  cifrasLabel = 'Cifras',
  cifras = [],
  paraQuienLabel = 'Para quién',
  paraQuien,
  stackLabel = 'Stack',
  stack = [],
  enlacesLabel = 'Enlaces',
  enlaces = [],
  ctaText = '¿Tienes un reto parecido?',
}) {
  return (
    <>
      <SEOHead title={seoTitle} description={seoDescription} path={path} jsonLd={jsonLd} locale={locale} alternates={alternates} />
      <article className="pt-32 pb-24 v-container" data-testid={testId}>
        <Breadcrumb items={breadcrumbItems || [{ label: locale === 'en' ? 'Home' : 'Inicio', to: locale === 'en' ? '/en' : '/' }, { label: breadcrumbLabel }]} />

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl mb-14">
          {tag && <span className="v-tag green mb-6 inline-flex">{tag}</span>}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black dark:text-white/95 mb-6 tracking-tight leading-tight">
            {titleMain}{' '}
            {titleAccent && (
              <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: '#8DC21F' }}>{titleAccent}</span>
            )}
          </h1>
          <p className="text-base sm:text-lg text-gray-700 dark:text-white/65 leading-relaxed">{lead}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mb-16">
          <div className="lg:col-span-2 space-y-10">
            {problema && (
              <section>
                <h2 className="text-xl font-bold text-black dark:text-white/90 mb-3">{problemaLabel}</h2>
                <p className="text-sm sm:text-base text-gray-700 dark:text-white/65 leading-relaxed">{problema}</p>
              </section>
            )}

            {comoFunciona.length > 0 && (
              <section>
                <h2 className="text-xl font-bold text-black dark:text-white/90 mb-4">{comoFuncionaLabel}</h2>
                <p className="text-sm sm:text-base text-gray-700 dark:text-white/65 leading-relaxed">{comoFunciona}</p>
              </section>
            )}

            {cifras.length > 0 && (
              <section>
                <h2 className="text-xl font-bold text-black dark:text-white/90 mb-4">{cifrasLabel}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {cifras.map((c, i) => (
                    <div key={i} className="glass-card p-4 text-center">
                      <div className="text-lg sm:text-xl font-bold" style={{ color: '#8DC21F' }}>{c.value}</div>
                      <div className="text-[10px] uppercase tracking-wide text-gray-600 dark:text-white/50">{c.label}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {paraQuien && (
              <section>
                <h2 className="text-xl font-bold text-black dark:text-white/90 mb-3">{paraQuienLabel}</h2>
                <p className="text-sm sm:text-base text-gray-700 dark:text-white/65 leading-relaxed">{paraQuien}</p>
              </section>
            )}
          </div>

          <aside className="space-y-6">
            {stack.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-white/40 mb-3">{stackLabel}</h3>
                <div className="flex flex-wrap gap-2">
                  {stack.map((s) => (
                    <span key={s} className="text-[11px] font-semibold px-2.5 py-1 rounded-full text-gray-600 dark:text-white/60" style={{ border: '1px solid var(--v-border)' }}>{s}</span>
                  ))}
                </div>
              </div>
            )}

            {enlaces.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-white/40 mb-3">{enlacesLabel}</h3>
                <div className="flex flex-col gap-2.5 text-sm">
                  {enlaces.map((l, i) => (
                    l.external ? (
                      <a key={i} href={l.to} target="_blank" rel="noopener noreferrer" className="hover:text-[#8DC21F] transition-colors">{l.label}</a>
                    ) : (
                      <Link key={i} to={l.to} className="hover:text-[#8DC21F] transition-colors">{l.label}</Link>
                    )
                  ))}
                </div>
              </div>
            )}

            <Link to={locale === 'en' ? '/en/contact' : '/contacto'} className="btn-primary w-full flex items-center justify-center gap-2" data-testid={`${testId}-cta-contact`}>
              {ctaText} <ArrowRight size={15} />
            </Link>
          </aside>
        </div>
      </article>
    </>
  );
}
